import { useEffect, useState } from 'react';

export default function Hero() {
  const [displayedText, setDisplayedText] = useState('');
  const fullText = 'UI/UX Designer';

  useEffect(() => {
    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setDisplayedText(fullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-rose-50/30 to-slate-50 overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Large Circle Gradient */}
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-rose-300/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-rose-200/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        
        {/* Floating Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, #9f1239 1px, transparent 1px)', backgroundSize: '50px 50px' }}></div>
      </div>

      {/* Scattered Design Elements */}
      <div className="absolute top-20 right-32 w-32 h-32 rounded-2xl overflow-hidden opacity-60 rotate-45 border-2 border-rose-200 shadow-lg animate-float-slow">
        <img
          src="https://public.readdy.ai/ai/img_res/659d6c569db4aedce71f9f94d8c5e1a8.jpg"
          alt="Design Element"
          className="w-full h-full object-cover object-top"
        />
      </div>

      <div className="absolute top-1/4 left-20 w-24 h-24 rounded-full overflow-hidden opacity-50 -rotate-[60deg] border-2 border-rose-200 shadow-lg animate-float-medium">
        <img
          src="https://readdy.ai/api/search-image?query=minimal%20user%20flow%20diagram%20with%20simple%20arrows%20and%20boxes%20clean%20black%20and%20white%20sketch%20UX%20design%20process%20flowchart%20hand%20drawn%20professional%20design%20thinking%20methodology&width=96&height=96&seq=hero-sketch-userflow-002&orientation=squarish"
          alt="Design Element"
          className="w-full h-full object-cover object-top"
        />
      </div>

      <div className="absolute bottom-40 right-1/4 w-28 h-28 rounded-2xl overflow-hidden opacity-55 rotate-[135deg] border-2 border-rose-200 shadow-lg animate-float-fast">
        <img
          src="https://readdy.ai/api/search-image?query=clean%20simple%20UI%20component%20library%20sketch%20buttons%20input%20fields%20cards%20minimal%20black%20line%20drawings%20on%20white%20background%20professional%20design%20system%20elements%20organized%20grid%20layout&width=112&height=112&seq=hero-sketch-components-003&orientation=squarish"
          alt="Design Element"
          className="w-full h-full object-cover object-top"
        />
      </div>

      <div className="absolute top-1/3 right-16 w-20 h-20 rounded-xl overflow-hidden opacity-45 -rotate-45 border-2 border-rose-200 shadow-lg animate-float-medium">
        <img
          src="https://readdy.ai/api/search-image?query=minimalist%20color%20palette%20swatches%20clean%20geometric%20shapes%20simple%20color%20theory%20design%20minimal%20professional%20UI%20color%20scheme%20organized%20layout%20black%20and%20white%20with%20subtle%20accent%20colors&width=80&height=80&seq=hero-sketch-colors-004&orientation=squarish"
          alt="Design Element"
          className="w-full h-full object-cover object-top"
        />
      </div>

      <div className="absolute bottom-32 left-1/4 w-36 h-24 rounded-2xl overflow-hidden opacity-50 rotate-[60deg] border-2 border-rose-200 shadow-lg animate-float-slow">
        <img
          src="https://readdy.ai/api/search-image?query=simple%20typography%20specimen%20showing%20different%20font%20weights%20and%20sizes%20minimal%20black%20text%20on%20white%20background%20clean%20professional%20type%20scale%20design%20system%20documentation&width=144&height=96&seq=hero-sketch-typography-005&orientation=landscape"
          alt="Design Element"
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Main Content Container */}
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-20 w-full relative z-10">
        <div className="text-center">
          {/* Name & Title */}
          <h1 className="text-6xl lg:text-7xl font-bold text-slate-900 mb-4 tracking-tight">
            Charit Patel
          </h1>

          {/* Animated Subtitle with Underline */}
          <div className="inline-block mb-8">
            <div className="text-2xl lg:text-3xl font-semibold mb-2" style={{ color: '#6b3d52' }}>
              {displayedText}
              <span className="animate-pulse">|</span>
            </div>
            <div className="h-1 rounded-full" style={{ background: 'linear-gradient(to right, transparent, #6b3d52, transparent)' }}></div>
          </div>

          {/* Professional Description */}
          <div className="max-w-3xl mx-auto mb-8">
            <p className="text-lg lg:text-xl text-slate-700 mb-4 leading-relaxed">
              I design intuitive digital experiences that bridge user needs with business goals. 
              With a focus on research driven solutions and clean interfaces, I help teams create 
              products that users love and stakeholders trust.
            </p>
            <p className="text-base lg:text-lg text-slate-600 leading-relaxed">
              Specializing in user research, interaction design, and design systems. 
              Turning complex problems into simple, elegant solutions.
            </p>
          </div>

          {/* Handwritten Note */}
          <div className="mb-10 -rotate-3">
            <p className="text-4xl lg:text-5xl font-handwriting inline-block" style={{ fontFamily: "'Caveat', cursive", color: '#7f1d1d' }}>
              your app/website needs me
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 justify-center mb-10">
            <a
              href="#projects"
              className="px-8 py-4 text-white text-base font-semibold rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
              style={{ backgroundColor: '#6b3d52' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7d4a60'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6b3d52'}
            >
              View My Work
            </a>
            <a
              href="#contact"
              className="px-8 py-4 bg-white text-base font-semibold border-2 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
              style={{ color: '#6b3d52', borderColor: '#6b3d52' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fdf2f8'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
            >
              Get in Touch
            </a>
          </div>

          {/* Social Links - Horizontal Layout */}
          <div className="flex gap-5 justify-center">
            <a
              href="https://www.linkedin.com/in/charit-patel-2533581ba/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 flex items-center justify-center rounded-xl hover:scale-110 hover:shadow-lg transition-all duration-300 group cursor-pointer"
              style={{ backgroundColor: '#6b3d52' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7d4a60'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6b3d52'}
            >
              <i className="ri-linkedin-fill text-xl text-white"></i>
            </a>
            <a
              href="https://www.behance.net/charitpatel06"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 flex items-center justify-center rounded-xl hover:scale-110 hover:shadow-lg transition-all duration-300 group cursor-pointer"
              style={{ backgroundColor: '#6b3d52' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7d4a60'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6b3d52'}
            >
              <i className="ri-behance-fill text-xl text-white"></i>
            </a>
            <a
              href="https://www.instagram.com/ui_charit/?hl=en"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 flex items-center justify-center rounded-xl hover:scale-110 hover:shadow-lg transition-all duration-300 group cursor-pointer"
              style={{ backgroundColor: '#6b3d52' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7d4a60'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6b3d52'}
            >
              <i className="ri-instagram-line text-xl text-white"></i>
            </a>
            <a
              href="https://drive.google.com/file/d/1DPnQPLuQV0RZ1qr9IElNjn8I9_n5eWgd/view?usp=sharing"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 flex items-center justify-center rounded-xl hover:scale-110 hover:shadow-lg transition-all duration-300 group cursor-pointer"
              style={{ backgroundColor: '#6b3d52' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7d4a60'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6b3d52'}
            >
              <i className="ri-download-line text-xl text-white"></i>
            </a>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="flex flex-col items-center gap-2">
          <span className="text-sm text-slate-500 font-medium">Scroll Down</span>
          <i className="ri-arrow-down-line text-2xl" style={{ color: '#6b3d52' }}></i>
        </div>
      </div>
    </section>
  );
}
